import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { assetStatusLabel, truncateText } from "@/components/equipment/equipmentLabels";
import { canHandoverAsset, canReturnAsset } from "@/lib/equipmentApi";
import type { EquipmentAsset } from "@/types/equipment";

interface EquipmentInventoryTableProps {
  assets: EquipmentAsset[];
  canEdit: boolean;
  saving: boolean;
  onEdit: (asset: EquipmentAsset) => void;
  onHandover: (asset: EquipmentAsset) => void;
  onReturn: (asset: EquipmentAsset) => void;
  onRetire: (asset: EquipmentAsset) => void;
}

export function EquipmentInventoryTable({
  assets,
  canEdit,
  saving,
  onEdit,
  onHandover,
  onReturn,
  onRetire,
}: EquipmentInventoryTableProps) {
  const { t } = useTranslation();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("admin.equipment.fields.name")}</TableHead>
          <TableHead>{t("admin.equipment.fields.serial")}</TableHead>
          <TableHead>{t("admin.equipment.fields.condition")}</TableHead>
          <TableHead>{t("admin.equipment.fields.status")}</TableHead>
          <TableHead>{t("admin.equipment.fields.actions")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {assets.map((asset) => (
          <TableRow key={asset.id}>
            <TableCell className="font-medium">{asset.name}</TableCell>
            <TableCell>{asset.serial_number || "—"}</TableCell>
            <TableCell className="max-w-[220px] text-sm text-muted-foreground">{truncateText(asset.notes, 90)}</TableCell>
            <TableCell>
              <Badge variant={asset.status === "available" ? "secondary" : "outline"}>
                {assetStatusLabel(asset.status)}
              </Badge>
            </TableCell>
            <TableCell className="space-x-2 whitespace-nowrap">
              {canEdit && (
                <Button size="sm" variant="outline" onClick={() => onEdit(asset)}>
                  {t("admin.equipment.edit")}
                </Button>
              )}
              {canEdit && canHandoverAsset(asset) && (
                <Button size="sm" variant="outline" onClick={() => onHandover(asset)}>
                  {t("admin.equipment.handover")}
                </Button>
              )}
              {canEdit && canReturnAsset(asset) && (
                <Button size="sm" variant="outline" onClick={() => onReturn(asset)}>
                  {t("admin.equipment.return")}
                </Button>
              )}
              {canEdit && canHandoverAsset(asset) && (
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => onRetire(asset)}>
                  {t("admin.equipment.retire")}
                </Button>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
